// Boards and streams — keys, defaults and Definition of Ready
// (docs/plans/2026-10-02-borden-ontwerp.md §3.4).

import type { WorkflowDefinition } from '../types/workflow.js'
import {
  STREAM_COLORS,
  type DefinitionOfReadyCheck,
  type QueueLane,
  type Severity,
  type StreamDefinition,
  type StreamLanes,
} from './types.js'

export const PROJECT_KEY_RE = /^[A-Z][A-Z0-9]{1,9}$/
export const TICKET_PREFIX_RE = /^[A-Z][A-Z0-9]{0,9}$/

/** Same rules as goblic_derive_project_key (migration 0023): letters/digits of the name, upper case, 3 chars, 'P' prefix when it starts with a digit, padded to 2 with 'X'; taken → base + 2, 3, ... */
export function deriveProjectKey(name: string, taken: readonly string[]): string {
  let base = (name ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  if (!/^[A-Z]/.test(base)) base = `P${base}`
  base = base.slice(0, 3)
  if (base.length < 2) base = base.padEnd(2, 'X')
  const used = new Set(taken)
  let candidate = base
  let n = 1
  while (used.has(candidate)) {
    n += 1
    candidate = `${base}${n}`
  }
  return candidate
}

/** Default ticket prefix of a stream: the project key for the default workflow, else the first 3 letters/digits of the workflow key upper-cased ('T' + 2 when it starts with a digit). Same as migration 0023. */
export function deriveTicketPrefix(workflowKey: string, opts: { isDefault: boolean; projectKey: string }): string {
  if (opts.isDefault) return opts.projectKey
  const stripped = workflowKey.replace(/[^a-zA-Z0-9]/g, '')
  if (/^[a-z]/.test(workflowKey)) return stripped.slice(0, 3).toUpperCase()
  return `T${stripped.slice(0, 2).toUpperCase()}`
}

/** 'GOB-190'; null without a ticket number. */
export function formatTicketKey(prefix: string | null | undefined, ticketNo: number | null | undefined): string | null {
  if (ticketNo === null || ticketNo === undefined || !prefix) return null
  return `${prefix}-${ticketNo}`
}

/** Whether a workflow is bug-like (templateKey 'bug', or key 'bug'/'bugs'). */
export function isBugWorkflow(workflow: Pick<WorkflowDefinition, 'key' | 'templateKey'>): boolean {
  return workflow.templateKey === 'bug' || workflow.key === 'bug' || workflow.key === 'bugs'
}

/** Settings of a stream Goblic creates for a workflow (new projects / new workflows). */
export function defaultStreamFor(
  workflow: Pick<WorkflowDefinition, 'key' | 'name' | 'templateKey'>,
  opts: { isDefault: boolean; projectKey: string; position: number },
): Omit<StreamDefinition, 'id' | 'projectId' | 'workflowMissing' | 'createdAt' | 'updatedAt'> {
  const bug = isBugWorkflow(workflow)
  const colors = STREAM_COLORS.filter((c) => c !== 'danger')
  const common = {
    key: workflow.key,
    name: workflow.name || workflow.key,
    ticketPrefix: deriveTicketPrefix(workflow.key, { isDefault: opts.isDefault, projectKey: opts.projectKey }),
    newTaskLabel: null,
    workflowKey: workflow.key,
    position: opts.position,
    countHumanSteps: true,
    paused: false,
    weeklyBudgetCents: null,
  }
  if (bug) {
    return {
      ...common,
      color: 'danger',
      lanes: { inbox: true, backlogReady: false },
      order: 'severity_age',
      wipLimit: 5,
      autoPull: true,
      expedite: { severity: 'S1', overWip: 1 },
      definitionOfReady: [],
    }
  }
  return {
    ...common,
    color: colors[Math.max(0, opts.position) % colors.length]!,
    lanes: { inbox: true, backlogReady: true },
    order: 'manual',
    wipLimit: 4,
    autoPull: false,
    expedite: null,
    definitionOfReady: ['acceptance_criteria', 'no_open_blockers'],
  }
}

/** The fallback criterion the API writes when a task has none. */
export const FALLBACK_ACCEPTANCE_CRITERION = 'Meets the instructions and all tests pass.'

export function evaluateDefinitionOfReady(
  checks: readonly DefinitionOfReadyCheck[],
  task: { acceptanceCriteria: string[]; openBlockerCount: number; severity: Severity | null },
): { passed: boolean; failed: DefinitionOfReadyCheck[] } {
  const failed: DefinitionOfReadyCheck[] = []
  for (const check of checks) {
    if (check === 'acceptance_criteria') {
      if (!task.acceptanceCriteria.some((c) => c !== FALLBACK_ACCEPTANCE_CRITERION)) failed.push(check)
    } else if (check === 'no_open_blockers') {
      if (task.openBlockerCount !== 0) failed.push(check)
    } else if (check === 'severity_set') {
      if (task.severity === null) failed.push(check)
    }
  }
  return { passed: failed.length === 0, failed }
}

/** Where new tasks land: inbox when the stream has one, else backlog. */
export function entryLane(lanes: StreamLanes): QueueLane {
  return lanes.inbox ? 'inbox' : 'backlog'
}

/** The lane the scheduler pulls from: ready with Backlog + Ready, else inbox. */
export function pullLane(lanes: StreamLanes): QueueLane {
  return lanes.backlogReady ? 'ready' : 'inbox'
}

export function laneEnabled(lanes: StreamLanes, lane: QueueLane): boolean {
  return lane === 'inbox' ? lanes.inbox : lanes.backlogReady
}
